import React from 'react';
import { Button, ListItemText, Menu, MenuItem } from '@mui/material';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useI18n } from '../i18n/LanguageContext';
import { localeOrder, translations } from '../i18n/translations';

const shortLabel: Record<string, string> = { en: 'EN', pt: 'PT', es: 'ES' };

interface LanguageSelectorProps {
  variant?: 'light' | 'dark';
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'light' }) => {
  const { locale, setLocale, t } = useI18n();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isDark = variant === 'dark';

  return (
    <>
      <Button
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<TranslateRoundedIcon />}
        endIcon={<KeyboardArrowDownRoundedIcon />}
        aria-label={t.header.languageLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{
          borderRadius: 999,
          px: { xs: 1.25, sm: 1.75 },
          color: isDark ? '#FFFFFF' : '#17351F',
          border: isDark ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(23, 53, 31, 0.26)',
          bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255, 255, 255, 0.72)',
          '& .MuiButton-endIcon': { ml: 0.25 },
          '&:hover': {
            borderColor: isDark ? '#FFFFFF' : 'rgba(23, 53, 31, 0.5)',
            bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255, 255, 255, 0.9)'
          }
        }}
      >
        {shortLabel[locale]}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {localeOrder.map((code) => (
          <MenuItem
            key={code}
            selected={code === locale}
            onClick={() => {
              setLocale(code);
              setAnchorEl(null);
            }}
            sx={{ minWidth: 180, gap: 1.5 }}
          >
            <ListItemText
              primary={translations[code].localeLabel}
              secondary={shortLabel[code]}
              secondaryTypographyProps={{ sx: { fontSize: '0.72rem' } }}
            />
            {code === locale && <CheckRoundedIcon fontSize="small" color="primary" />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;
